import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const euro = (v) =>
  (v ?? 0).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  });

const FORM_VUOTO = {
  unit_id: '',
  person_id: '',
  reference_date: '',
  amount: '',
  description: '',
  source: '',
  verified: false,
};

/**
 * Situazione al subentro: quanto ogni condòmino deve al condominio (o il condominio
 * a lui) alla data in cui la nuova amministrazione prende in carico la gestione.
 * È la prima riga di ogni estratto conto: senza, gli arretrati ereditati dalla
 * gestione precedente non trovano posto da nessuna parte.
 */
export default function OpeningBalances() {
  const { selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const ruolo = selectedCondominium?.role;
  const puoScrivere = ruolo === 'ADMIN' || ruolo === 'ACCOUNTANT';

  const [saldi, setSaldi] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);
  const [unita, setUnita] = useState([]);
  const [soggetti, setSoggetti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [dialogoAperto, setDialogoAperto] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [form, setForm] = useState(FORM_VUOTO);

  const carica = useCallback(async () => {
    if (!condoId) return;
    setCaricamento(true);
    try {
      const [s, r, u, p] = await Promise.all([
        axios.get(`${API}/condominiums/${condoId}/opening-balances`),
        axios.get(`${API}/condominiums/${condoId}/opening-balances/summary`),
        axios.get(`${API}/condominiums/${condoId}/units`),
        axios.get(`${API}/condominiums/${condoId}/people`),
      ]);
      setSaldi(s.data || []);
      setRiepilogo(r.data);
      setUnita(u.data || []);
      setSoggetti(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dei saldi di apertura');
    } finally {
      setCaricamento(false);
    }
  }, [condoId]);

  useEffect(() => { carica(); }, [carica]);

  const apriNuovo = () => {
    setInModifica(null);
    setForm({ ...FORM_VUOTO, reference_date: new Date().toISOString().slice(0, 10) });
    setDialogoAperto(true);
  };

  const apriModifica = (s) => {
    setInModifica(s);
    setForm({
      unit_id: s.unit_id,
      person_id: s.person_id || '',
      reference_date: s.reference_date || '',
      amount: String(s.amount ?? ''),
      description: s.description || '',
      source: s.source || '',
      verified: !!s.verified,
    });
    setDialogoAperto(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    const body = {
      unit_id: form.unit_id,
      person_id: form.person_id || null,
      reference_date: form.reference_date,
      amount: parseFloat(String(form.amount).replace(',', '.')),
      description: form.description || null,
      source: form.source || null,
      verified: !!form.verified,
    };
    if (Number.isNaN(body.amount)) {
      toast.error("L'importo non è un numero valido");
      return;
    }
    try {
      if (inModifica) {
        await axios.put(
          `${API}/condominiums/${condoId}/opening-balances/${inModifica.id}`, body);
        toast.success('Saldo aggiornato');
      } else {
        await axios.post(`${API}/condominiums/${condoId}/opening-balances`, body);
        toast.success('Saldo registrato');
      }
      setDialogoAperto(false);
      await carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const elimina = async (s) => {
    if (!window.confirm(
      `Eliminare il saldo di apertura di ${s.person_name || s.unit_number}?`)) return;
    try {
      await axios.delete(`${API}/condominiums/${condoId}/opening-balances/${s.id}`);
      toast.success('Saldo eliminato');
      await carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const scoperte = riepilogo
    ? (riepilogo.units_total || 0) - (riepilogo.units_covered || 0)
    : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Situazione al subentro"
        subtitle="Crediti e debiti di ogni condòmino alla data di passaggio delle consegne"
        actions={puoScrivere && (
          <Button onClick={apriNuovo} data-testid="add-opening-balance">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo saldo
          </Button>
        )}
      />

      {riepilogo && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Crediti verso i condòmini</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-700">
                {euro(riepilogo.total_credits)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                quanto i condòmini devono al condominio
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Debiti verso i condòmini</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-700">{euro(riepilogo.total_debts)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                quanto il condominio deve a loro
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Saldo netto</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{euro(riepilogo.net)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {riepilogo.reference_date
                  ? `alla data del ${riepilogo.reference_date}`
                  : 'nessuna data di riferimento'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Copertura</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {riepilogo.units_covered}/{riepilogo.units_total}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                unità con una posizione registrata
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {(scoperte > 0 || (riepilogo?.unverified_count ?? 0) > 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              {scoperte > 0 && (
                <p>
                  {scoperte} unità non hanno ancora un saldo di apertura registrato: per
                  queste posizioni l&apos;estratto conto parte da zero.
                </p>
              )}
              {(riepilogo?.unverified_count ?? 0) > 0 && (
                <p>
                  {riepilogo.unverified_count} saldi non sono ancora contrassegnati come
                  verificati sul documento di origine.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Posizioni registrate</CardTitle>
          <CardDescription>
            Importo positivo: il condòmino deve al condominio. Importo negativo: il
            condominio deve al condòmino.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {caricamento ? (
            <p className="py-8 text-center text-muted-foreground">Caricamento…</p>
          ) : saldi.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              Nessun saldo di apertura registrato.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unità</TableHead>
                    <TableHead>Soggetto</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Origine</TableHead>
                    {puoScrivere && <TableHead className="text-right">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {saldi.map((s) => (
                    <TableRow key={s.id} data-testid={`opening-balance-${s.id}`}>
                      <TableCell className="font-medium">{s.unit_number || '-'}</TableCell>
                      <TableCell>{s.person_name || '—'}</TableCell>
                      <TableCell>{s.reference_date}</TableCell>
                      <TableCell
                        className={`text-right tabular-nums font-medium ${
                          s.amount < 0 ? 'text-red-700' : 'text-emerald-700'
                        }`}
                      >
                        {euro(s.amount)}
                      </TableCell>
                      <TableCell>
                        {s.verified ? (
                          <Badge className="bg-emerald-100 text-emerald-800">Verificato</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-900">Da verificare</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-xs text-muted-foreground truncate" title={s.source || ''}>
                          {s.source || '-'}
                        </p>
                      </TableCell>
                      {puoScrivere && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => apriModifica(s)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-700"
                              onClick={() => elimina(s)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogoAperto} onOpenChange={setDialogoAperto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {inModifica ? 'Modifica saldo di apertura' : 'Nuovo saldo di apertura'}
            </DialogTitle>
            <DialogDescription>
              Registra la posizione del condòmino alla data di subentro, come risulta
              dal consuntivo della gestione precedente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={salva} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unità *</Label>
                <Select
                  value={form.unit_id || undefined}
                  onValueChange={(v) => setForm({ ...form, unit_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {unita.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.unit_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Soggetto</Label>
                <Select
                  value={form.person_id || undefined}
                  onValueChange={(v) => setForm({ ...form, person_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Facoltativo" /></SelectTrigger>
                  <SelectContent>
                    {soggetti.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data di riferimento *</Label>
                <Input
                  type="date"
                  value={form.reference_date}
                  onChange={(e) => setForm({ ...form, reference_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Importo (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="es. 1234,56 oppure -250,00"
                  required
                />
              </div>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Documento di origine</Label>
              <Input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="es. Conto economico 2025, p.3"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="verified"
                type="checkbox"
                checked={form.verified}
                onChange={(e) => setForm({ ...form, verified: e.target.checked })}
              />
              <Label htmlFor="verified" className="cursor-pointer">
                Importo verificato sul documento di origine
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogoAperto(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={!form.unit_id}>Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
